import {isCmsQueryObject, type TCmsQueryValue, type TCmsQueryVariables} from '../../cmsQueryTypes';

import type {IQueryBuilderConfig} from './IQueryBuilder';

// No aliased imports: also used by the SSR middleware (`server/middlewares/reactRenderer/urls.ts`).

/**
 * Builds ContentHub API query parameters from content IDs and variables.
 * Isomorphic: works on both client and server.
 */
export class QueryBuilder {
	private config: IQueryBuilderConfig;

	/**
	 * QueryBuilder Constructor
	 * @param {IQueryBuilderConfig} config - query config
	 */
	constructor(config: IQueryBuilderConfig) {
		this.config = config;
	}

	/**
	 * Merge brand configuration with core configuration.
	 * Brand-specific rules take precedence over core rules.
	 */
	setConfig(brandConfig: Partial<IQueryBuilderConfig>) {
		this.config = {
			idMappings: {
				...this.config.idMappings,
				...brandConfig.idMappings,
			},
			variableCaseHandlers: {
				...this.config.variableCaseHandlers,
				...brandConfig.variableCaseHandlers,
			},
			parseObjectRules: {
				...this.config.parseObjectRules,
				...brandConfig.parseObjectRules,
			},
			defaultResultConfig: {
				...this.config.defaultResultConfig,
				...brandConfig.defaultResultConfig,
			},
		};
	}

	private parseObject = (key: string, value: TCmsQueryValue, res: TCmsQueryVariables) => {
		const {directKeys = [], useExistsForAllBooleans = false} = this.config.parseObjectRules;

		if (directKeys.includes(key)) {
			res[key] = value;
		} else if (Array.isArray(value)) {
			res[`${key}[in]`] = value.join(',');
		} else if (isCmsQueryObject(value)) {
			Object.keys(value).forEach((subKey) => {
				this.parseObject(`${key}.${subKey}`, value[subKey], res);
			});
		} else if ((value === false || value === true) && useExistsForAllBooleans) {
			res[`${key}[exists]`] = value;
		} else {
			res[`${key}[in]`] = value;
		}
	};

	/**
	 * Builds a flat ContentHub query-parameter object from a content id and variables.
	 */
	build(query: {id?: string; variables?: TCmsQueryVariables}): TCmsQueryVariables {
		const {id, variables = {}} = query;

		const res: TCmsQueryVariables = {
			include: variables?.include ?? this.config.defaultResultConfig.include,
			limit: variables?.limit ?? this.config.defaultResultConfig?.limit ?? undefined,
		};

		if (id) {
			let newID = id;

			if (id.startsWith('Get') && id !== 'GetAssets' && id !== 'GetEntries') {
				const slice3 = id.slice(3);

				newID = slice3.charAt(0).toLowerCase() + slice3.slice(1);
			}

			const mappingRule = this.config.idMappings[newID];

			if (mappingRule) {
				newID = mappingRule.newId;

				if (mappingRule.transformVariables) {
					mappingRule.transformVariables(variables);
				}
			}

			if (newID) {
				res.content_type = newID;
			}
		}

		if (variables) {
			variables.fields = {...(isCmsQueryObject(variables.fields) ? variables.fields : {})};

			// Read again at every use: a custom handler may replace `fields`.
			const getFields = () => variables.fields as TCmsQueryVariables;

			for (const el in variables) {
				const customHandler = this.config.variableCaseHandlers?.[el];

				if (customHandler) {
					customHandler(variables, el);

					continue;
				}

				if (el !== 'fields' && el !== 'sys' && el !== 'locale') {
					getFields()[el] = variables[el];

					delete variables[el];
				}
			}

			if (Object.keys(getFields()).length === 0) {
				delete variables.fields;
			}
		}

		if (variables) {
			Object.keys(variables).forEach((key) => {
				const value = variables[key];
				this.parseObject(key, value, res);
			});
		}

		return res;
	}
}

export default QueryBuilder;
